import { Navigate, useSearchParams } from "react-router";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorOverview } from "./logs/error-overview";
import { RawLogs } from "./logs/raw-logs";
import { InfrastructureTab } from "./infrastructure-tab";
import { EndpointsTab } from "./endpoints-tab";

const TABS = [
  { key: "feil", label: "Feiloversikt" },
  { key: "raa", label: "Rålogg" },
  { key: "infra", label: "Infrastruktur" },
  { key: "endepunkter", label: "Endepunkter" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

/**
 * Feiloversikt groups warn/error into signatures and is the day-to-day view.
 * Rålogg is the flat table — the only way to search info/debug and follow a
 * request id through non-error lines. Infrastruktur is node and Postgres
 * health; Endepunkter is request volume and latency per route.
 *
 * `range` is shared between Feiloversikt and Endepunkter on purpose: an error
 * spike and the latency of the routes behind it are one question.
 */
export function MonitoringPage() {
  const { hasRole } = useAuth();
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("tab");
  const tab: TabKey = TABS.some((t) => t.key === fromUrl)
    ? (fromUrl as TabKey)
    : "feil";

  if (!hasRole("superuser")) {
    return <Navigate to="/dashboard" replace />;
  }

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(params);
    next.set("tab", value);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Overvåking</h2>
      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="feil">
          <ErrorOverview />
        </TabsContent>
        <TabsContent value="raa">
          <RawLogs />
        </TabsContent>
        <TabsContent value="infra">
          <InfrastructureTab />
        </TabsContent>
        <TabsContent value="endepunkter">
          <EndpointsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
